export function extractPromptFromMessages(messages: any[]): string {
  if (!messages || messages.length === 0) {
    return "No prompt available";
  }

  const firstUserMessage = messages.find((msg: any) => msg.role === "user");
  if (!firstUserMessage || !firstUserMessage.content) {
    return "No prompt available";
  }

  const textContent = firstUserMessage.content.find(
    (c: any) => c.type === "text"
  );
  if (!textContent || !textContent.text) {
    return "No prompt available";
  }

  const text = textContent.text;

  // Try different patterns to extract the prompt

  // Pattern 1: Navigate a computer format
  const navigateMatch = text.match(
    /Navigate a computer using computer(?:_use)? to\n([\s\S]+)/
  );
  if (navigateMatch && navigateMatch[1]) {
    return navigateMatch[1].trim();
  }

  // Pattern 2: FreeCAD/Application-specific format with SYSTEM_CAPABILITY
  // Look for text after the SYSTEM_CAPABILITY block
  const systemCapMatch = text.match(
    /<\/SYSTEM_CAPABILITY>\s*\n\s*([\s\S]+?)(?:\n\s*$|$)/
  );
  if (systemCapMatch && systemCapMatch[1]) {
    return systemCapMatch[1].trim();
  }

  // Pattern 3: Look for task-like sentences that start with action verbs
  // These typically indicate the actual task description
  const taskPatterns = [
    // Match sentences starting with common task verbs
    /(?:^|\n)\s*((?:Open|Create|Navigate|Find|Click|Select|Save|Edit|Remove|Add|Change|Move|Copy|Delete|Set|Make|Draw|Insert|Export|Import|Build|Design|Write|Type|Enter|Press|Close|Drag|Drop|Resize|Rotate|Scale|Undo|Redo|Apply|Use|Go to|Download|Upload|Search|Replace|Merge|Split|Convert|Format|Align|Group|Ungroup|Lock|Unlock|Hide|Show|Zoom|Pan|Scroll|Expand|Collapse|Enable|Disable|Toggle|Check|Uncheck|Fill|Clear|Reset|Submit|Cancel|Confirm|Accept|Reject|Approve|Deny|Send|Receive|Connect|Disconnect|Start|Stop|Pause|Resume|Play|Record|Capture|Render|Preview|Publish|Deploy|Install|Uninstall|Update|Upgrade|Downgrade|Configure|Customize|Adjust|Modify|Transform|Animate|Duplicate|Clone|Mirror|Flip|Invert|Reverse|Sort|Filter|Organize|Arrange|Distribute|Stack|Layer|Nest|Embed|Link|Unlink|Attach|Detach|Pin|Unpin|Bookmark|Tag|Label|Name|Rename|Title|Describe|Annotate|Comment|Note|Mark|Highlight|Underline|Bold|Italic|Strikethrough|Superscript|Subscript|Indent|Outdent|Wrap|Unwrap|Break|Join|Trim|Crop|Slice|Dice|Chop|Cut|Paste|Punch|Extrude|Revolve|Sweep|Loft|Shell|Fillet|Chamfer|Offset|Thicken|Boolean|Subtract|Intersect|Union|Difference|Combine|Separate|Decompose|Explode|Implode|Assemble|Disassemble|Constraint|Dimension|Measure|Calculate|Compute|Evaluate|Analyze|Inspect|Verify|Validate|Test|Debug|Fix|Repair|Heal|Patch|Restore|Recover|Backup|Archive|Compress|Decompress|Encrypt|Decrypt|Sign|Verify|Authenticate|Authorize|Grant|Revoke|Allow|Block|Permit|Forbid|Restrict|Limit|Extend|Shorten|Lengthen|Widen|Narrow|Deepen|Flatten|Smooth|Sharpen|Blur|Brighten|Darken|Lighten|Saturate|Desaturate|Colorize|Decolorize|Grayscale|Sepia|Invert|Negate|Posterize|Solarize|Emboss|Engrave|Deboss|Bevel|Relief|Contour|Outline|Silhouette|Shadow|Glow|Shine|Sparkle|Twinkle|Fade|Dissolve|Wipe|Slide|Push|Pull|Squeeze|Stretch|Squash|Skew|Shear|Distort|Warp|Bend|Twist|Curl|Wave|Ripple|Bulge|Pinch|Punch|Spherize|Cylinderize|Polarize)[^.]*\.(?:[^.]*\.)*)/i
  ];

  for (const pattern of taskPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      // Found a task-like sentence, return it
      return match[1].trim();
    }
  }

  // Pattern 4: Look for the last paragraph that looks like a task
  // Split by double newlines and find paragraphs starting with action verbs
  const paragraphs = text.split(/\n\n+/);
  const actionVerbRegex = /^(Open|Create|Navigate|Find|Click|Select|Save|Edit|Remove|Add|Change|Move|Copy|Delete|Set|Make|Draw|Insert|Export|Import|Build|Design|Write|Type|Enter|Press|Close|Drag|Drop|Resize|Rotate|Scale)\b/i;

  for (let i = paragraphs.length - 1; i >= 0; i--) {
    const para = paragraphs[i].trim();
    if (para && actionVerbRegex.test(para)) {
      return para;
    }
  }

  // Pattern 5: If text contains list items (- MP4, - MOV, etc.),
  // look for content after the last list item
  const lastListItemIndex = text.lastIndexOf('\n- ');
  if (lastListItemIndex !== -1) {
    // Find the end of the last list item (next line that doesn't continue the item)
    const afterList = text.substring(lastListItemIndex);
    const lines = afterList.split('\n');
    let taskStart = -1;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      // If line doesn't start with - and has substantial content
      if (line && !line.startsWith('-') && line.length > 20 && actionVerbRegex.test(line)) {
        taskStart = i;
        break;
      }
    }

    if (taskStart !== -1) {
      return lines.slice(taskStart).join('\n').trim();
    }
  }

  // Pattern 6: Fallback - try to extract meaningful content
  // Skip any XML-like tags and list items
  const lines = text.split('\n');
  const meaningfulLines = lines.filter((line: string) => {
    const trimmed = line.trim();
    return trimmed &&
           !trimmed.startsWith('<') &&
           !trimmed.startsWith('*') &&
           !trimmed.startsWith('-') &&  // Skip list items
           trimmed.length > 10;
  });

  // If we have meaningful lines, check if the last ones look like a task
  if (meaningfulLines.length > 0) {
    // Check if any line starts with an action verb
    for (let i = meaningfulLines.length - 1; i >= 0; i--) {
      if (actionVerbRegex.test(meaningfulLines[i].trim())) {
        // Return from this line onwards
        return meaningfulLines.slice(i).join(' ').trim();
      }
    }
    // If no action verb found, return last meaningful line
    return meaningfulLines[meaningfulLines.length - 1].trim();
  }

  // Final fallback: return the full text if we can't extract a specific prompt
  return text.trim() || "No prompt available";
}